import { useMemo, useCallback } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { useAircraftGetAll } from './aircraft'

export interface MaintenanceAircraft {
  id: string
  registration: string
  start: Dayjs
  end: Dayjs
}

/**
 * Resolves aircraft maintenance windows for the roster views and returns a
 * per-day lookup. When a single aircraft is selected only that aircraft is
 * considered; otherwise ("All Aircraft") every aircraft is considered.
 *
 * `getMaintenanceForDay` returns the aircraft in maintenance on a given day
 * (deduped by id) so callers can both flag the day and list which aircraft.
 */
export function useAircraftMaintenance(aircraftIds?: string[]) {
  const { data: allAircraft = [] } = useAircraftGetAll()
  const selectedId = aircraftIds && aircraftIds.length === 1 ? aircraftIds[0] : null

  // Selected aircraft only, or all aircraft when "All Aircraft" is selected.
  const windows = useMemo(() => {
    const considered = selectedId
      ? allAircraft.filter((a) => a.id === selectedId)
      : allAircraft
    return considered.flatMap((a) =>
      (a.maintenanceRecords ?? []).map((r: any) => ({
        id: a.id,
        registration: a.registration as string,
        start: dayjs(r.startDate).startOf('day'),
        end: dayjs(r.endDate).endOf('day'),
      }))
    )
  }, [allAircraft, selectedId])

  // Aircraft in maintenance on `date`, deduped by aircraft id.
  const getMaintenanceForDay = useCallback(
    (date: Dayjs): MaintenanceAircraft[] => {
      const byId = new Map<string, MaintenanceAircraft>()
      for (const w of windows) {
        const inWindow =
          (date.isSame(w.start, 'day') || date.isAfter(w.start)) &&
          (date.isSame(w.end, 'day') || date.isBefore(w.end))
        if (inWindow && !byId.has(w.id)) byId.set(w.id, w)
      }
      return Array.from(byId.values())
    },
    [windows]
  )

  return { getMaintenanceForDay, hasMaintenance: windows.length > 0 }
}
